import { Injectable } from '@angular/core';
import { Validators } from '@angular/forms';
import {
  ControlsOf,
  FormArray,
  FormBuilder,
  FormGroup,
} from '@ngneat/reactive-forms';
import {
  ValidateClassificationRequired,
  ValidateEmail,
  ValidateIsValidPhoneNumber,
  ValidateSpace,
} from '@vinnova/validation';
import { produce } from 'immer';
import { RepeatableMinLength } from '../../components/question/repeater/validators/repeatable-min-length.validator';
import { WorksiteMax } from '../../components/question/worksite/validators/worksite-max.validator';
import { WorksiteMin } from '../../components/question/worksite/validators/worksite-min.validator';
import { WorksiteRequired } from '../../components/question/worksite/validators/worksite-required.validator';
import {
  FormForm,
  FormPerson,
  FormQuestion,
  FormQuestionFollowUp,
  FormQuestionPerson,
  FormQuestionRepeater,
  FormQuestionStandard,
  FormRepeatable,
  FormSection,
} from '../models/form-group-mapping.model';
import { Form } from '../models/form.model';
import { QuestionStructureOptions } from '../models/question/question-options.model';
import {
  Person,
  QuestionStructurePerson,
} from '../models/question/question-person.model';
import {
  QuestionStructureRepeater,
  Repeatable,
} from '../models/question/question-repeater.model';
import { QuestionStructureTable } from '../models/question/question-table.model';
import { QuestionType } from '../models/question/question-type.model';
import { QuestionStructureWorksite } from '../models/question/question-worksite.model';
import { Question } from '../models/question/question.model';
import { Section } from '../models/section.model';
import { RendererQuestionsQuery } from '../queries/renderer-questions.query';
import { RendererFormsRepository } from '../repositories/renderer-forms.repository';
import { RendererQuestionsRepository } from '../repositories/renderer-questions.repository';
import { ValidationMapperService } from './validation-mapper.service';

@Injectable({ providedIn: 'root' })
export class DynamicFormControlService {
  constructor(
    private fb: FormBuilder,
    private validationMapper: ValidationMapperService,
    private rendererQuestionsQuery: RendererQuestionsQuery,
    private rendererQuestionsRepository: RendererQuestionsRepository,
    private rendererFormsRepository: RendererFormsRepository,
  ) {}

  formToFormGroup(form: Form): FormGroup<ControlsOf<FormForm>> {
    return this.fb.group({
      id: form.id,
      sections: this.createSectionsArray(form.sections),
    });
  }

  createSectionsArray(sections: Section[]): FormArray<FormSection> {
    const sectionsArray = this.fb.array<FormSection>([]);
    const hideSections = this.rendererFormsRepository.getHideSections();
    const filteredSections = sections.filter(
      (s) => !hideSections?.includes(s.id),
    );
    for (const s of filteredSections) {
      sectionsArray.push(this.sectionQuestionToFormGroup(s));
    }
    return sectionsArray;
  }

  sectionQuestionToFormGroup(
    section: Section,
  ): FormGroup<ControlsOf<FormSection>> {
    const questions = [];
    for (const questionId of section.structure.order.flat()) {
      questions.push(this.rendererQuestionsRepository.get(questionId));
    }

    return this.fb.group({
      id: section.id,
      questions: this.createQuestionsArray(questions),
    });
  }

  createQuestionsArray(questions: Question[]): FormArray<FormQuestion> {
    const questionsArray = this.fb.array<FormQuestion>([]);
    for (const o of questions) {
      questionsArray.push(this.formQuestionToFormGroup(o));
    }
    return questionsArray;
  }

  formQuestionToFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestion>> {
    switch (question?.type) {
      case QuestionType.RadioButton:
      case QuestionType.Dropdown:
      case QuestionType.Checkbox: {
        return this.parseOptionsFormGroup(question);
      }
      case QuestionType.Repeater: {
        return this.parseRepeatablesFormGroup(question);
      }
      case QuestionType.Worksite: {
        return this.parseWorksiteFormGroup(question);
      }
      case QuestionType.Person: {
        return this.parsePersonFormGroup(question);
      }
      case QuestionType.Classification: {
        return this.parseClassificationFormGroup(question);
      }
      case QuestionType.Datepicker: {
        return this.parseDateFormGroup(question);
      }
      case QuestionType.DatepickerChangeRequest: {
        return this.parseDateChangeRequestFormGroup(question);
      }
      case QuestionType.Information: {
        return this.fb.group({
          id: question.id,
          value: undefined,
        });
      }
      default: {
        return this.fb.group({
          id: question?.id,
          value: [
            question?.value,
            this.validationMapper.getValueValidation(question),
          ],
        });
      }
    }
  }

  private parseOptionsFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestionFollowUp>> {
    const questionStructureOptions =
      question.structure as QuestionStructureOptions;
    const questionArray = this.fb.array<FormQuestion>([]);
    if (questionStructureOptions.followUpQuestionsOrder) {
      const followUpQuestions =
        this.rendererQuestionsQuery.getQuestionsFromOrder(
          questionStructureOptions.followUpQuestionsOrder,
        );
      for (const question of followUpQuestions) {
        questionArray.push(this.formQuestionToFormGroup(question));
      }
    }

    return this.fb.group({
      id: question.id,
      value: [
        question.value,
        this.validationMapper.getValueValidation(question),
      ],
      followUpQuestions: questionArray.length > 0 ? questionArray : undefined,
    });
  }

  private parseRepeatablesFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestionRepeater>> {
    const repeatables = this.fb.array<FormRepeatable>([]);
    const questionStructureRepeater =
      question.structure as QuestionStructureRepeater;

    // TODO: Ta bort när enkätregistret försvinner
    if (globalThis.location.href.includes('enkatregister')) {
      for (const repeatable of questionStructureRepeater.repeatables) {
        repeatables.push(this.createRepeatableFormGroup(repeatable));
      }
    }

    if (questionStructureRepeater.minNumberOfRepeatables) {
      repeatables.addValidators(
        RepeatableMinLength(questionStructureRepeater.minNumberOfRepeatables),
      );
    }

    return this.fb.group({
      id: question.id,
      repeatables: repeatables,
      value: question.value,
    });
  }

  createRepeatableFormGroup(
    repeatable: Repeatable,
  ): FormGroup<ControlsOf<FormRepeatable>> {
    const repeatableQuestions = this.rendererQuestionsQuery.getQuestions(
      repeatable.repeatableQuestions.map((q) => q.id),
    );
    const mappedRepeatableQuestions = this.mapRepeatableAnswerData(
      repeatableQuestions,
      repeatable,
    );

    return this.fb.group({
      id: repeatable.id,
      questions: mappedRepeatableQuestions,
    });
  }

  mapRepeatableAnswerData(
    repeatableQuestions: Question[],
    repeatable: Repeatable,
  ): FormArray<FormQuestion> {
    const mappedQuestions = this.fb.array<FormQuestion>([]);

    for (const question of repeatableQuestions) {
      const repeatableQuestion = repeatable.repeatableQuestions.find(
        (q) => q.id === question.id,
      );

      if (!repeatableQuestion.answerData) {
        mappedQuestions.push(this.formQuestionToFormGroup(question));
      } else if (question.type === QuestionType.InputTable) {
        const mappedQuestion = produce(question, (draft) => {
          const inputTableStructure = draft.structure as QuestionStructureTable;
          for (const tableQuestion of inputTableStructure.questions) {
            const questionAnswer = repeatableQuestion.answerData.find(
              (d) =>
                d.rowId === tableQuestion.rowId &&
                d.columnId === tableQuestion.columnId,
            );

            tableQuestion.value = questionAnswer.value;
          }
        });
        mappedQuestions.push(this.formQuestionToFormGroup(mappedQuestion));
      } else if (question.type) {
        mappedQuestions.push(
          this.formQuestionToFormGroup(
            produce(question, (draft) => {
              draft.value =
                repeatableQuestion.answerData.value ??
                repeatableQuestion.answerData;
            }),
          ),
        );
      }
    }

    return mappedQuestions;
  }

  private parseWorksiteFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestionStandard>> {
    const required =
      question.validation.required &&
      (question.structure as QuestionStructureWorksite)?.oregType !==
        'isAccount'
        ? WorksiteRequired
        : question.validation.required
          ? Validators.required
          : undefined;
    const maxLength = question.validation.maxLength
      ? WorksiteMax(question.validation.maxLength)
      : Validators.max(undefined);
    const minLength = question.validation.minLength
      ? WorksiteMin(question.validation.minLength)
      : Validators.min(0);
    return required
      ? this.fb.group({
          id: question.id,
          value: [question.value, [required, maxLength, minLength]],
        })
      : this.fb.group({
          id: question.id,
          value: [question.value, [maxLength, minLength]],
        });
  }

  private parsePersonFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestionPerson>> {
    const maxLength = question.validation.maxLength
      ? Validators.maxLength(question.validation.maxLength)
      : Validators.maxLength(25);
    const minLength = question.validation.required
      ? Validators.minLength(1)
      : Validators.minLength(0);
    const required = question.validation.required
      ? Validators.required
      : undefined;

    const validators = [maxLength, minLength];

    if (required) {
      validators.push(required);
    }

    return this.fb.group({
      id: question.id,
      value: [question.value, required],
      persons: this.fb.array([], validators),
    });
  }

  private parseDateFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestion>> {
    return this.fb.group({
      id: question.id,
      value: [
        question?.value,
        this.validationMapper.getValueValidation(question),
      ],
      fromDate: '',
      toDate: '',
    });
  }

  private parseDateChangeRequestFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestion>> {
    return this.fb.group({
      id: question.id,
      value: [
        question?.value,
        this.validationMapper.getValueValidation(question),
      ],
      newReportDate: '',
      newProjectEndDate: '',
    });
  }

  private parseClassificationFormGroup(
    question: Question,
  ): FormGroup<ControlsOf<FormQuestion>> {
    return this.fb.group({
      id: question.id,
      value: [question.value, ValidateClassificationRequired],
    });
  }

  createPersonFormGroup(
    required: boolean,
    person?: Person,
    structure?: QuestionStructurePerson,
  ): FormGroup<ControlsOf<FormPerson>> {
    const id = person ? person.id : undefined;
    const firstName = person ? person.firstName : undefined;
    const lastName = person ? person.lastName : undefined;
    const email = person ? person.email : undefined;
    const phone = person ? person.phone : undefined;
    const sex = person ? person.sex : undefined;
    const role = person ? person.role : undefined;

    const firstNameRequired =
      required && structure.firstName
        ? [Validators.required, ValidateSpace]
        : undefined;
    const lastNameRequired =
      required && structure.lastName
        ? [Validators.required, ValidateSpace]
        : undefined;
    const emailValidation =
      required && structure.email
        ? [Validators.required, ValidateEmail]
        : [ValidateEmail];
    const phoneRequired =
      required && structure.phone
        ? [Validators.required, ValidateIsValidPhoneNumber]
        : [ValidateIsValidPhoneNumber];
    const sexRequired =
      required && structure.sex ? Validators.required : undefined;
    const roleRequired =
      required && structure.role
        ? [Validators.required, ValidateSpace]
        : undefined;

    const firstNameControl = this.fb.control(firstName, firstNameRequired);
    const lastNameControl = this.fb.control(lastName, lastNameRequired);
    const emailControl = this.fb.control(email, emailValidation);
    const phoneControl = this.fb.control(phone, phoneRequired);
    const sexControl = this.fb.control(sex, sexRequired);
    const roleControl = this.fb.control(role, roleRequired);

    if (firstName) {
      firstNameControl.markAsTouched();
      firstNameControl.markAsDirty();
    }
    if (lastName) {
      lastNameControl.markAsTouched();
      lastNameControl.markAsDirty();
    }
    if (email) {
      emailControl.markAsTouched();
      emailControl.markAsDirty();
    }
    if (phone) {
      phoneControl.markAsTouched();
      phoneControl.markAsDirty();
    }
    if (sex) {
      sexControl.markAsTouched();
      sexControl.markAsDirty();
    }
    if (role) {
      roleControl.markAsTouched();
      roleControl.markAsDirty();
    }

    return this.fb.group({
      id: id,
      firstName: firstNameControl,
      lastName: lastNameControl,
      email: emailControl,
      phone: phoneControl,
      sex: sexControl,
      role: roleControl,
    });
  }
}
